// The shared building blocks. Native elements first: a real <button>, a real
// <dialog> (focus trapped, Esc closes), real labels, so the a11y lint and
// assistive technology both see what they expect.
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { useEffect, useId, useRef } from 'react';

import { ApiError, messageOf } from '../api/client';
import type { BuildState } from '../api/generated/BuildState';
import type { Schedule } from '../api/generated/Schedule';
import type { MistIcon } from './mist-icons';
import { MIST_ICONS } from './mist-icons';

/**
 * MIST.Web's buttons (ADR-0075): `outlined` is MudBlazor's outlined primary,
 * the everyday action; `primary` the filled accent, a view's one main action;
 * `text` a quiet one, as Sign out is in MIST's header; `danger` an action that
 * stops or reports something.
 */
type Variant = 'outlined' | 'primary' | 'text' | 'danger';

export function Button({
    variant = 'outlined',
    large = false,
    icon,
    className,
    type = 'button',
    children,
    ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; large?: boolean; icon?: MistIcon }) {
    const classes = ['sf-button', `sf-button--${variant}`, large ? 'sf-button--large' : '', className ?? '']
        .filter(Boolean)
        .join(' ');
    return (
        <button type={type} className={classes} {...props}>
            {icon ? <Icon name={icon} /> : null}
            {children}
        </button>
    );
}

/** A Material icon, drawn exactly as MIST.Web draws it (generated from MudBlazor's own). */
export function Icon({ name }: { name: MistIcon }) {
    return (
        <svg
            className="sf-icon"
            viewBox="0 0 24 24"
            aria-hidden="true"
            focusable="false"
            // The markup is a compile-time constant from mist-icons.ts, never input.
            dangerouslySetInnerHTML={{ __html: MIST_ICONS[name] }}
        />
    );
}

/** MIST's plain-type brand mark: a letter on the accent, and the name beside it. */
export function BrandMark({ showName = true }: { showName?: boolean }) {
    return (
        <span className="sf-brand">
            <span className="sf-brand-mark" aria-hidden="true">
                M
            </span>
            {showName ? <span className="sf-brand-name text-md">MIST</span> : null}
        </span>
    );
}

/**
 * The top of every page, as MIST.Web's PageHeader: an eyebrow, the page's one
 * h1, an optional description and an optional row of actions.
 */
export function PageHeader({
    id,
    eyebrow = 'Shop floor',
    title,
    description,
    actions,
}: {
    id?: string;
    eyebrow?: string;
    title: ReactNode;
    description?: ReactNode;
    actions?: ReactNode;
}) {
    return (
        <header className="sf-page-header">
            <div className="sf-page-header-text">
                <p className="eyebrow sf-page-header-eyebrow">{eyebrow}</p>
                <h1 id={id}>{title}</h1>
                {description ? <p className="sf-page-header-description">{description}</p> : null}
            </div>
            {actions ? <div className="sf-page-header-actions">{actions}</div> : null}
        </header>
    );
}

/** MIST's status tones; the label always carries the meaning, never colour alone. */
type Tone = 'neutral' | 'success' | 'pending' | 'danger';

export function Badge({ tone = 'neutral', children }: { tone?: Tone; children: ReactNode }) {
    return <span className={`sf-pill sf-pill--${tone}`}>{children}</span>;
}

const STATE_LABEL: Record<BuildState, string> = {
    Ready: 'Ready',
    InProgress: 'In progress',
    OnHold: 'On hold',
    Built: 'Built',
};
// As MIST.Web shows a build (StatusLabels.ShopFloorBuildTone).
const STATE_TONE: Record<BuildState, Tone> = {
    Ready: 'pending',
    InProgress: 'pending',
    OnHold: 'danger',
    Built: 'success',
};

export function StateBadge({ state }: { state: BuildState }) {
    return <Badge tone={STATE_TONE[state]}>{STATE_LABEL[state]}</Badge>;
}

export const stateLabel = (state: BuildState): string => STATE_LABEL[state];

export function ScheduleBadge({ schedule, reason }: { schedule: Schedule; reason?: string | null }) {
    return schedule === 'OnTime' ? (
        <Badge tone="success">On time</Badge>
    ) : (
        <Badge tone="danger">At risk{reason ? <span className="sf-visually-hidden">: {reason}</span> : null}</Badge>
    );
}

export function ProgressBar({ done, total, label }: { done: number; total: number; label: string }) {
    const percent = total === 0 ? 0 : Math.round((done / total) * 100);
    return (
        <div
            className="sf-progress"
            role="progressbar"
            aria-label={label}
            aria-valuemin={0}
            aria-valuemax={total}
            aria-valuenow={done}
        >
            <span style={{ width: `${String(percent)}%` }} />
        </div>
    );
}

export function Loading({ what }: { what: string }) {
    return (
        <p className="sf-muted" role="status" aria-live="polite">
            Loading {what}…
        </p>
    );
}

/** A service that gave no usable answer, as MIST.Web's ServiceUnavailable shows one. */
export function ErrorPanel({ error, title }: { error: unknown; title?: string }) {
    const trace = error instanceof ApiError ? error.problem.traceId : '';
    return (
        <div className="sf-unavailable" role="alert">
            <p className="text-md sf-unavailable-title">{title ?? 'That didn’t work.'}</p>
            <p className="sf-unavailable-description">{messageOf(error)}</p>
            {trace ? <p className="text-mono sf-muted">Reference {trace}</p> : null}
        </div>
    );
}

export function EmptyState({ children }: { children: ReactNode }) {
    return (
        <div className="sf-card sf-empty-state">
            <p className="sf-empty-state-description">{children}</p>
        </div>
    );
}

/** A visitor sees everything read-only, with a way in where they could act (O4), as MIST.Web's ReadOnlyHint. */
export function SignInHint({ onSignIn }: { onSignIn: () => void }) {
    return (
        <div className="sf-note sf-note--info sf-row sf-no-print">
            <span>You’re browsing read-only.</span>
            <Button icon="login" onClick={onSignIn}>
                Sign in as Technician to build
            </Button>
        </div>
    );
}

export function Dialog({
    open,
    title,
    onClose,
    children,
}: {
    open: boolean;
    title: string;
    onClose: () => void;
    children: ReactNode;
}) {
    const ref = useRef<HTMLDialogElement>(null);
    const titleId = useId();
    useEffect(() => {
        const dialog = ref.current;
        if (!dialog) return;
        if (open && !dialog.open) dialog.showModal();
        if (!open && dialog.open) dialog.close();
    }, [open]);
    return (
        <dialog ref={ref} className="sf-dialog" aria-labelledby={titleId} onClose={onClose}>
            <div className="sf-dialog-body">
                <h2 id={titleId}>{title}</h2>
                {open ? children : null}
            </div>
        </dialog>
    );
}

export function TextField({
    label,
    value,
    onChange,
    errors = [],
    multiline = false,
    type = 'text',
    required = false,
    inputMode,
    placeholder,
}: {
    label: string;
    value: string;
    onChange: (value: string) => void;
    errors?: readonly string[];
    multiline?: boolean;
    type?: 'text' | 'number' | 'date' | 'datetime-local';
    required?: boolean;
    inputMode?: 'decimal' | 'text';
    placeholder?: string;
}) {
    const id = useId();
    const errorId = `${id}-errors`;
    const described = errors.length > 0 ? { 'aria-describedby': errorId, 'aria-invalid': true } : {};
    return (
        <div className="sf-field">
            <label htmlFor={id}>{label}</label>
            {multiline ? (
                <textarea
                    id={id}
                    className="sf-textarea"
                    value={value}
                    required={required}
                    placeholder={placeholder}
                    onChange={(event) => {
                        onChange(event.target.value);
                    }}
                    {...described}
                />
            ) : (
                <input
                    id={id}
                    className="sf-input"
                    type={type}
                    value={value}
                    required={required}
                    inputMode={inputMode}
                    placeholder={placeholder}
                    onChange={(event) => {
                        onChange(event.target.value);
                    }}
                    {...described}
                />
            )}
            {errors.length > 0 ? (
                <div id={errorId} className="sf-field-error">
                    {errors.join(' ')}
                </div>
            ) : null}
        </div>
    );
}
